import React, { useCallback } from 'react';

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';

import { DropZoneIndicator } from './DropZoneIndicator';

import type { DraggedItem } from '../../hooks/useLineItemDrag';
import type { UnifiedFeeItem, UnifiedOtherService } from '../../types';

export interface DraggableLineItemProps {
	id: string;
	item?: UnifiedFeeItem | UnifiedOtherService;
	categoryId?: string;
	children: React.ReactNode;
	disabled?: boolean;
	isParent?: boolean; // Whether this row can accept children
	draggedItemHasChildren?: boolean; // Whether the currently dragged item has children
	isDraggingActive?: boolean; // Whether any item is currently being dragged
	isRecentlyDropped?: boolean; // Whether this item was recently dropped
	canUndo?: boolean; // Whether this item can be undone
	onUndo?: (item?: UnifiedFeeItem | UnifiedOtherService) => void;
	isUndoing?: boolean; // Whether an undo operation is in progress
	onDropAsChild?: (droppedItem: DraggedItem) => void;
	onDragStart?: () => void;
	onDragEnd?: () => void;
}

export const DraggableLineItem: React.FC<DraggableLineItemProps> = ({
	id,
	item,
	categoryId,
	children,
	disabled = false,
	isParent = false,
	draggedItemHasChildren = false,
	isDraggingActive = false,
	isRecentlyDropped = false,
	canUndo = false,
	onUndo,
	isUndoing = false,
	onDropAsChild,
	onDragStart,
	onDragEnd,
}) => {
	// Always use the id prop directly for consistency with AnalysisStep
	// AnalysisStep expects the draggable ID to match what's passed in the id prop
	const draggable = useDraggable({
		id, // Use the provided id prop (e.g., item.id or uniqueRowId)
		disabled,
		data: {
			item,
			categoryId,
		},
	});

	const isDragging = draggable.isDragging;
	const attributes = draggable.attributes;
	const listeners = draggable.listeners;
	const transform = draggable.transform
		? {
				x: draggable.transform.x,
				y: draggable.transform.y,
			}
		: null;

	// Only enable droppable for parent rows that can accept children
	// AnalysisStep expects line-item-${id} format for droppable items
	// Enable droppable when:
	// 1. This is a parent row (can accept children)
	// 2. We're not currently dragging this item itself
	// 3. The dragged item has no children (can be nested)
	const isValidDropTarget = isParent && !draggedItemHasChildren;
	// Only disable if we're dragging this item itself, or if it's not a valid drop target
	const shouldDisableDroppable = isDragging || !isValidDropTarget;

	const droppable = useDroppable({
		id: `line-item-${id}`, // AnalysisStep expects this format
		data: {
			item,
			categoryId,
			type: 'line-item',
		},
		disabled: shouldDisableDroppable,
	});

	const isOver = droppable.isOver;
	const setDroppableRef = droppable.setNodeRef;
	const setDraggableRef = draggable.setNodeRef;

	// Combine refs
	const setNodeRef = useCallback(
		(node: HTMLElement | null) => {
			setDraggableRef(node);
			setDroppableRef(node);
		},
		[setDraggableRef, setDroppableRef],
	);

	const style = transform
		? {
				transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
				opacity: isDragging ? 0.5 : 1,
				zIndex: isDragging ? 999 : undefined,
			}
		: undefined;

	// Show drop indicator only for parent rows that can accept children (valid drop)
	// This shows when actively hovering over a valid drop target
	const showDropHighlight =
		isOver && !isDragging && isParent && !draggedItemHasChildren;

	// Show invalid drop indicator when dragging a parent with children over another parent
	const showInvalidDropHighlight =
		isDraggingActive &&
		!isDragging &&
		isParent &&
		draggedItemHasChildren &&
		isOver;

	// Show potential valid drop zone indicator when dragging a child item (no children) near but not over
	// This provides visual feedback that this row can accept the drop
	const showPotentialDropZone =
		isDraggingActive &&
		!isDragging &&
		isParent &&
		!draggedItemHasChildren &&
		!isOver;

	// Determine the appropriate class for visual feedback
	const getDropHighlightClass = () => {
		if (showDropHighlight) {
			return 'bg-blue-100 ring-2 ring-blue-500 ring-inset relative z-10 rounded-sm shadow-md';
		}
		if (showInvalidDropHighlight) {
			return 'bg-red-100 ring-2 ring-red-400 ring-inset relative z-10 rounded-sm cursor-not-allowed';
		}
		if (showPotentialDropZone) {
			return 'ring-1 ring-blue-200 ring-inset';
		}
		if (isRecentlyDropped) {
			return 'bg-green-100 ring-2 ring-green-400 ring-inset relative z-10 rounded-sm animate-pulse';
		}
		return '';
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			className={`transition-colors duration-200 ${getDropHighlightClass()}`}
		>
			{/* Drop indicator line at top for valid drop targets */}
			{showDropHighlight && (
				<div className="absolute top-0 left-0 right-0 h-1.5 bg-blue-500 rounded-full shadow-sm z-10" />
			)}
			<div className="relative flex items-center gap-1 pl-2">
				{!disabled && (
					<button
						{...listeners}
						className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-200 rounded touch-none"
						style={{ touchAction: 'none' }}
					>
						<GripVertical className="w-4 h-4 text-gray-400" />
					</button>
				)}
				{children}
			</div>
			{/* Invalid drop indicator icon */}
			{showInvalidDropHighlight && (
				<div className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-lg pointer-events-none">
					🚫
				</div>
			)}
			{/* Drop zone indicator for child drops - show "Drop as child" message above the row */}
			{showDropHighlight && (
				<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex items-center justify-center pointer-events-none z-20">
					<div className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-semibold animate-pulse flex items-center gap-2">
						<span>↓</span>
						<span>Drop as child</span>
						<span>↓</span>
					</div>
				</div>
			)}
			{/* Legacy drop zone indicator (kept for compatibility) */}
			<DropZoneIndicator
				type="item-child"
				isVisible={
					isOver && isParent && !draggedItemHasChildren && !showDropHighlight
				}
				position="inside"
			/>
		</div>
	);
};
